import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { generateClient } from 'aws-amplify/api';
import {
  DNABox,
  DNAButton,
  DNACard,
  DNAText,
  GenericToast,
  TextField,
  ToastOrientations,
  useToast,
} from '@alucio/lux-ui';
import {
  ApplyDocumentMappingMutation,
  DocumentMappingInput,
  DocumentMappingPreview,
  DOCUMENT_MAPPING_ACTION,
  DOCUMENT_MAPPING_STATUS,
  PreviewDocumentMappingQuery,
} from '@alucio/aws-beacon-amplify/src/API';
import { applyDocumentMapping } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { previewDocumentMapping } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { ToastActions } from '@alucio/lux-ui/lib/components/Toast/useToast';
import { HelpCircleTooltip } from 'src/components/DNA';
import { TenantORM } from 'src/state/redux/selector/tenant';

type DocumentMappingProps = {
  selectedTenant: TenantORM,
  integrationType?: string | null,
}

const PARTIAL_RESULT_MESSAGE = 'Some records were updated. Refresh the preview before retrying.';
const REQUEST_FAILURE_MESSAGE = 'Unable to complete the document mapping request. Try previewing the changes again.';
const DOCUMENT_MAPPING_INTEGRATION_TYPES = ['BEACON', 'VEEVA'];

type DocumentMappingToast = {
  description: string,
  status: 'error' | 'success' | 'warning',
  title: string,
}

export const supportsDocumentMapping = (integrationType?: string | null): boolean =>
  DOCUMENT_MAPPING_INTEGRATION_TYPES.includes(integrationType || '');

export const canRenderDocumentMapping = (
  hasSelectedIntegration: boolean,
  integrationType?: string | null,
): boolean => hasSelectedIntegration && supportsDocumentMapping(integrationType);

export const showDocumentMappingToast = (
  toast: Pick<ToastActions, 'add'> | undefined,
  { description, status, title }: DocumentMappingToast,
): void => {
  toast?.add(
    <GenericToast description={description} status={status} title={title} />,
    ToastOrientations.TOP_RIGHT,
  );
};

const buildMappingInput = ({
  action,
  externalId,
  selectedTenant,
  sourceDocumentVersionId,
  targetDocumentVersionId,
}: {
  action: DOCUMENT_MAPPING_ACTION,
  externalId: string,
  selectedTenant: TenantORM,
  sourceDocumentVersionId?: string,
  targetDocumentVersionId?: string,
}): DocumentMappingInput => ({
  action,
  externalId: externalId.trim(),
  sourceDocumentVersionId: sourceDocumentVersionId || undefined,
  targetDocumentVersionId: action === DOCUMENT_MAPPING_ACTION.LINK
    ? targetDocumentVersionId?.trim() || undefined
    : undefined,
  tenantId: selectedTenant.tenant.id,
});

const DocumentMapping: React.FC<DocumentMappingProps> = ({ selectedTenant, integrationType }) => {
  const [action, setAction] = useState<DOCUMENT_MAPPING_ACTION>(DOCUMENT_MAPPING_ACTION.LINK);
  const [linkExternalId, setLinkExternalId] = useState('');
  const [unlinkExternalId, setUnlinkExternalId] = useState('');
  const [targetDocumentVersionId, setTargetDocumentVersionId] = useState('');
  const [sourceDocumentVersionId, setSourceDocumentVersionId] = useState<string>();
  const [previewInput, setPreviewInput] = useState<DocumentMappingInput>();
  const [preview, setPreview] = useState<DocumentMappingPreview>();
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [requiresRefresh, setRequiresRefresh] = useState(false);
  const previewRequestVersion = useRef(0);
  const toast = useToast();

  const clearPreview = useCallback(() => {
    previewRequestVersion.current += 1;
    setPreview(undefined);
    setPreviewInput(undefined);
    setSourceDocumentVersionId(undefined);
    setRequiresRefresh(false);
    setIsPreviewing(false);
  }, []);

  const canPreviewLink = Boolean(
    linkExternalId.trim() &&
    targetDocumentVersionId.trim() &&
    !isPreviewing &&
    !isApplying,
  );
  const canPreviewUnlink = Boolean(
    unlinkExternalId.trim() &&
    !isPreviewing &&
    !isApplying,
  );
  const requiresSourceSelection = (preview?.sourceVersions.length || 0) > 1;
  const hasBlockingPreviewError = preview?.blockers.some(blocker =>
    blocker !== 'AMBIGUOUS_SOURCE_VERSION',
  );
  const canApply = Boolean(
    preview &&
    previewInput &&
    !hasBlockingPreviewError &&
    (!requiresSourceSelection || sourceDocumentVersionId),
  ) && !isApplying && !isPreviewing && !requiresRefresh;

  const handlePreview = useCallback(async (nextAction: DOCUMENT_MAPPING_ACTION) => {
    const externalId = nextAction === DOCUMENT_MAPPING_ACTION.LINK
      ? linkExternalId
      : unlinkExternalId;
    const input = buildMappingInput({
      action: nextAction,
      externalId,
      selectedTenant,
      targetDocumentVersionId,
    });
    const canPreview = Boolean(
      input.externalId &&
      (nextAction === DOCUMENT_MAPPING_ACTION.UNLINK || input.targetDocumentVersionId) &&
      !isPreviewing &&
      !isApplying,
    );

    if (!canPreview) return;

    const requestVersion = previewRequestVersion.current + 1;
    previewRequestVersion.current = requestVersion;
    setAction(nextAction);
    setIsPreviewing(true);
    setPreview(undefined);
    setPreviewInput(undefined);
    setSourceDocumentVersionId(undefined);

    try {
      const appsyncClient = generateClient<PreviewDocumentMappingQuery>();
      const { data } = await appsyncClient.graphql({
        query: previewDocumentMapping,
        variables: { input },
      });

      if (!data.previewDocumentMapping) {
        throw new Error('The preview did not return a result.');
      }

      if (previewRequestVersion.current !== requestVersion) return;

      setPreview(data.previewDocumentMapping);
      setPreviewInput(input);
    }
    catch {
      if (previewRequestVersion.current !== requestVersion) return;

      showDocumentMappingToast(toast, {
        description: REQUEST_FAILURE_MESSAGE,
        status: 'error',
        title: 'Preview failed',
      });
    }
    finally {
      if (previewRequestVersion.current === requestVersion) {
        setIsPreviewing(false);
      }
    }
  }, [isApplying, isPreviewing, linkExternalId, selectedTenant, targetDocumentVersionId, toast, unlinkExternalId]);

  const handleApply = useCallback(async () => {
    if (!canApply || !previewInput) return;

    setIsApplying(true);

    try {
      const appsyncClient = generateClient<ApplyDocumentMappingMutation>();
      const { data } = await appsyncClient.graphql({
        query: applyDocumentMapping,
        variables: {
          input: {
            ...previewInput,
            sourceDocumentVersionId: sourceDocumentVersionId || undefined,
          },
        },
      });
      const result = data.applyDocumentMapping;

      if (!result) {
        throw new Error('The apply request did not return a result.');
      }

      const isPartialResult = result.status === DOCUMENT_MAPPING_STATUS.PARTIAL;
      setRequiresRefresh(isPartialResult);
      showDocumentMappingToast(toast, {
        description: isPartialResult
          ? PARTIAL_RESULT_MESSAGE
          : result.message || 'Document mapping updated.',
        status: isPartialResult ? 'warning' : 'success',
        title: isPartialResult ? 'Document mapping partially updated' : 'Document mapping updated',
      });
    }
    catch {
      showDocumentMappingToast(toast, {
        description: REQUEST_FAILURE_MESSAGE,
        status: 'error',
        title: 'Document mapping failed',
      });
    }
    finally {
      setIsApplying(false);
    }
  }, [canApply, previewInput, sourceDocumentVersionId, toast]);

  if (!supportsDocumentMapping(integrationType)) {
    return null;
  }

  return (
    <DNACard appearance="outline" style={styles.card}>
      <DNABox appearance="col" spacing="lg" style={styles.content}>
        <DNABox appearance="col" spacing="xs" style={styles.header}>
          <DNABox alignY="center" appearance="row" spacing="xs">
            <DNAText bold>Document Mapping</DNAText>
            <HelpCircleTooltip
              popoverText={
                'Use this tool to move or remove Veeva or Beacon integration metadata without deleting documents.'
              }
            />
          </DNABox>
          <DNAText status="tertiary">
            Move existing Veeva or Beacon integration metadata to another Beacon document.
          </DNAText>
        </DNABox>

        <DNABox appearance="row" spacing="lg" style={styles.mappingActions}>
          <DNABox appearance="col" spacing="sm" style={styles.mappingColumn}>
            <DNAText bold>Linking Documents</DNAText>
            <DNABox alignY="center" appearance="row" spacing="xs">
              <DNAText status="danger">*</DNAText>
              <DNAText label>Veeva external ID</DNAText>
              <HelpCircleTooltip popoverText="Use the external document ID from the Veeva or Beacon integration." />
            </DNABox>
            <TextField.Kitten
              onChangeText={(value) => {
                setLinkExternalId(value);
                clearPreview();
              }}
              placeholder="External document ID"
              value={linkExternalId}
            />
            <DNABox alignY="center" appearance="row" spacing="xs">
              <DNAText status="danger">*</DNAText>
              <DNAText label>Beacon document version ID</DNAText>
              <HelpCircleTooltip
                popoverText="Use the Beacon version ID, not the document title or version number."
              />
            </DNABox>
            <TextField.Kitten
              onChangeText={(value) => {
                setTargetDocumentVersionId(value);
                clearPreview();
              }}
              placeholder="Beacon document version ID"
              value={targetDocumentVersionId}
            />
            <DNAButton
              appearance="outline"
              disabled={!canPreviewLink}
              onPress={() => handlePreview(DOCUMENT_MAPPING_ACTION.LINK)}
              status="secondary"
            >
              {isPreviewing && action === DOCUMENT_MAPPING_ACTION.LINK ? 'Previewing link...' : 'Link document'}
            </DNAButton>
          </DNABox>
          <DNABox appearance="col" spacing="sm" style={styles.mappingColumn}>
            <DNAText bold>Unlinking Documents</DNAText>
            <DNABox alignY="center" appearance="row" spacing="xs">
              <DNAText status="danger">*</DNAText>
              <DNAText label>Veeva external ID</DNAText>
              <HelpCircleTooltip popoverText="Use the external document ID from the Veeva or Beacon integration." />
            </DNABox>
            <TextField.Kitten
              onChangeText={(value) => {
                setUnlinkExternalId(value);
                clearPreview();
              }}
              placeholder="External document ID"
              value={unlinkExternalId}
            />
            <DNAButton
              appearance="outline"
              disabled={!canPreviewUnlink}
              onPress={() => handlePreview(DOCUMENT_MAPPING_ACTION.UNLINK)}
              status="secondary"
            >
              {isPreviewing && action === DOCUMENT_MAPPING_ACTION.UNLINK ? 'Previewing unlink...' : 'Unlink document'}
            </DNAButton>
          </DNABox>
        </DNABox>

        {preview && (
          <DNABox appearance="col" spacing="sm" style={styles.review}>
            <DNAText bold>Review changes</DNAText>
            <DNAText status="tertiary">
              {action === DOCUMENT_MAPPING_ACTION.LINK
                ? 'Apply moves the detected metadata to the destination and clears the previous records.'
                : 'Apply clears matching metadata. It does not delete documents.'}
            </DNAText>
            {preview.blockers.map(blocker => (
              <DNAText key={blocker} status="danger">{blocker}</DNAText>
            ))}
            {requiresSourceSelection && (
              <DNABox appearance="col" spacing="xs">
                <DNAText>Select the source document version.</DNAText>
                {preview.sourceVersions.map(source => (
                  <DNAButton
                    appearance={sourceDocumentVersionId === source.documentVersionId ? 'filled' : 'outline'}
                    key={source.documentVersionId}
                    onPress={() => setSourceDocumentVersionId(source.documentVersionId)}
                    status="secondary"
                  >
                    {source.documentVersionId}
                  </DNAButton>
                ))}
              </DNABox>
            )}
            {preview.sourceVersions.length === 1 && (
              <DNAText>Source version: {preview.sourceVersions[0].documentVersionId}</DNAText>
            )}
            {action === DOCUMENT_MAPPING_ACTION.LINK && preview.targetDocumentVersionId && (
              <DNAText>Destination version: {preview.targetDocumentVersionId}</DNAText>
            )}
            <DNAText>Documents to clear: {preview.documentsToClear.length}</DNAText>
            <DNAText>Versions to clear: {preview.versionsToClear.length}</DNAText>
            {preview.status === DOCUMENT_MAPPING_STATUS.NO_CHANGE && (
              <DNAText status="tertiary">No matching mapping was found.</DNAText>
            )}
            <DNAButton
              disabled={!canApply}
              onPress={handleApply}
              status="secondary"
            >
              {isApplying ? 'Applying mapping...' : 'Apply mapping'}
            </DNAButton>
          </DNABox>
        )}

      </DNABox>
    </DNACard>
  );
};

const styles = StyleSheet.create({
  card: {
    marginTop: 24,
    padding: 24,
  },
  content: {
    width: '100%',
  },
  header: {
    borderBottomColor: '#EBEBEB',
    borderBottomWidth: 1,
    paddingBottom: 24,
  },
  mappingActions: {
    flexWrap: 'wrap',
  },
  mappingColumn: {
    flex: 1,
    minWidth: 320,
  },
  review: {
    borderTopColor: '#EBEBEB',
    borderTopWidth: 1,
    paddingTop: 24,
  },
});

export default DocumentMapping;
